/**
 * onViewportEnter - Triggers callback when element enters viewport
 * Uses Intersection Observer API to detect when element is approaching viewport
 *
 * @param {HTMLElement} element            - Element to observe
 * @param {Function}    callback           - Function to call when element enters viewport
 * @param {Object}      options            - Intersection Observer options
 * @param {string}      options.rootMargin - Margin around viewport (default: '200px')
 * @param {number}      options.threshold  - Intersection threshold (default: 0)
 * @return {Function} Cleanup function to disconnect observer
 *
 * @example
 * const cleanup = onViewportEnter(myElement, () => {
 *   console.log('Element is near viewport!');
 * });
 * // Later: cleanup() to disconnect observer
 */
const onViewportEnter = (element, callback, options = {}) => {
  const defaultOptions = {
    rootMargin: '200px', // Trigger 200px before entering viewport
    threshold:  0,
    ...options,
  };

  let hasTriggered = false;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      // Only trigger once when entering viewport
      if (entry.isIntersecting && !hasTriggered) {
        hasTriggered = true;
        callback(entry.target);
        observer.disconnect();
      }
    });
  }, defaultOptions);

  observer.observe(element);

  // Return cleanup function
  return () => observer.disconnect();
};

export default onViewportEnter;
